/**
 * Global keyboard shortcuts for the app shell (FLOW-778).
 *
 * Cmd/Ctrl+J toggles the chat drawer; Escape closes it; a `g` press followed
 * within CHORD_WINDOW_MS by a letter in NAV_CHORDS navigates.
 *
 * Cmd/Ctrl+K toggles the command palette from anywhere, text fields
 * included — the browser gives it no default and the palette is global
 * (FLOW-798). `/` opens it too, but only outside text fields and with no
 * modifier held, so typing a slash still types a slash. While the palette
 * is open, this handler owns Escape itself: it clears the query on the
 * first press and closes the palette on the second, regardless of where
 * focus is, and the drawer underneath stays put (FLOW-858 — previously the
 * palette's own input handled Escape, so it did nothing at all once focus
 * left the input).
 *
 * Single-character keys are matched case-insensitively. `KeyboardEvent.key`
 * reports the *typed character*, so Caps Lock (or a held Shift, which
 * `hasModifier` deliberately does not reject) turns `j` into `J` and `g`/`l`
 * into `G`/`L`; without folding case the shortcuts silently stopped working.
 * Named keys like `Escape` are multi-character and left untouched.
 *
 * A held Shift also fires its own keydown (`key: "Shift"`) before the letter
 * it modifies. While a chord is armed, keydowns for bare modifier keys are
 * ignored without disarming, so `g` then a held Shift then `L` still
 * navigates instead of the Shift keydown eating the arm.
 */

/**
 * Nav destinations that render nothing until a project is selected — the
 * shell's content gate swaps every one of them for the WelcomeWizard. Both
 * the sidebar rail and the chord handler below consult this, so a user who
 * has not created a project yet cannot be sent to a surface that only shows
 * the wizard back (FLOW-985).
 */
export const PROJECT_SCOPED_PATHS: readonly string[] = [
  "/plan",
  "/board",
  "/backlog",
  "/documents",
  "/decisions",
  "/chat",
];

export const needsProject = (path: string): boolean =>
  PROJECT_SCOPED_PATHS.includes(path);

export const NAV_CHORDS: Record<string, string> = {
  p: "/plan",
  b: "/board",
  l: "/backlog",
  d: "/documents",
  c: "/chat",
};

export const CHORD_WINDOW_MS = 800;

export const modKey = (): string =>
  navigator.platform?.includes("Mac") ? "⌘" : "Ctrl+";

export const isTextTarget = (e: KeyboardEvent): boolean => {
  const target = e.target as HTMLElement | null;
  if (!target) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" || tag === "TEXTAREA" || Boolean(target.isContentEditable)
  );
};

const hasModifier = (e: KeyboardEvent): boolean =>
  e.metaKey || e.ctrlKey || e.altKey;

const MODIFIER_KEYS = new Set(["Shift", "Control", "Alt", "Meta", "CapsLock"]);
const isModifierKey = (e: KeyboardEvent): boolean => MODIFIER_KEYS.has(e.key);

export interface ShellKeyDeps {
  navigate: (path: string) => void;
  toggleDrawer: () => void;
  closeDrawer: () => void;
  drawerOpen: () => boolean;
  paletteOpen: () => boolean;
  setPaletteOpen: (open: boolean) => void;
  paletteQuery: () => string;
  setPaletteQuery: (q: string) => void;
  /**
   * Whether a project still has to be selected before the project-scoped
   * destinations render anything (FLOW-985). Required, unlike `now` below:
   * an absent `now` falls back to `Date.now` and changes nothing, whereas an
   * absent guard would default to *permissive* and silently re-enable chords
   * into surfaces that only render the WelcomeWizard. Every caller states it.
   */
  projectRequired: () => boolean;
  now?: () => number;
}

export function createShellKeyHandler(
  deps: ShellKeyDeps,
): (e: KeyboardEvent) => void {
  const now = deps.now ?? Date.now;
  let pendingAt: number | null = null;

  return (e: KeyboardEvent) => {
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

    if ((e.metaKey || e.ctrlKey) && key === "j") {
      if (isTextTarget(e)) return;
      e.preventDefault();
      deps.toggleDrawer();
      return;
    }

    if ((e.metaKey || e.ctrlKey) && key === "k") {
      e.preventDefault();
      deps.setPaletteOpen(!deps.paletteOpen());
      return;
    }

    if (key === "/" && !hasModifier(e) && !isTextTarget(e)) {
      if (deps.paletteOpen()) return;
      e.preventDefault();
      deps.setPaletteOpen(true);
      return;
    }

    if (e.key === "Escape") {
      pendingAt = null;
      // This handler owns Escape outright while the palette is open, and
      // acts on it — clear the query first, close on the next press — before
      // the drawer branch below ever runs, so the drawer underneath does not
      // also close for that same keypress (FLOW-798 D3).
      //
      // Doing this here, rather than in the palette's own input handler, is
      // what makes it work regardless of focus (FLOW-858): the palette's
      // Escape branch only ever saw the keypress when its input itself was
      // focused, so focus anywhere else in the dialog (or beyond it) did
      // nothing at all — not even the first-press clear.
      //
      // This does not need to check `e.defaultPrevented` to read
      // `paletteOpen()` truthfully. The caller (AppShell) registers this
      // handler in the *capture* phase, which runs before any bubble-phase
      // or delegated listener — including ChatInput's own Escape handler —
      // so this branch always runs, and returns, before anything else has
      // touched the event. Guarding on `defaultPrevented` instead was tried
      // and reverted: it is not palette-specific, so it also suppressed
      // this branch whenever some *other* focused element (e.g. ChatInput)
      // preventDefault'd its own Escape handling (FLOW-798).
      if (deps.paletteOpen()) {
        e.preventDefault();
        if (deps.paletteQuery()) {
          deps.setPaletteQuery("");
          return;
        }
        deps.setPaletteOpen(false);
        return;
      }
      if (deps.drawerOpen()) {
        e.preventDefault();
        deps.closeDrawer();
      }
      return;
    }

    if (pendingAt !== null) {
      if (isModifierKey(e)) return; // e.g. a held Shift firing its own keydown — stay armed

      const armedAt = pendingAt;
      pendingAt = null;
      const path = NAV_CHORDS[key];
      if (
        path &&
        !hasModifier(e) &&
        !isTextTarget(e) &&
        now() - armedAt <= CHORD_WINDOW_MS
      ) {
        // The chord is consumed either way. With no project selected its
        // destination only renders the WelcomeWizard, so navigating would
        // move the URL and the rail highlight while the content stayed put
        // (FLOW-985) — swallow it instead of staging that no-op.
        e.preventDefault();
        if (!(deps.projectRequired() && needsProject(path))) {
          deps.navigate(path);
        }
      }
      return;
    }

    if (key === "g" && !hasModifier(e) && !isTextTarget(e)) {
      pendingAt = now();
    }
  };
}
